import { Button } from '@/components/ui/button'
import { useStocktakeScanLog } from '@/hooks/useStocktakes'
import { fmtDateTime } from '@/lib/dates'

const TH =
  'tbl-th border-b border-[var(--line-warm)] text-left'

/** Stocky's Log tab: every scan this session recorded, newest first, misses
 *  included. A miss is the point of the log — it's the record of what the
 *  operator scanned that this stocktake didn't recognise. */
export function StocktakeScanLog({ sessionId }: { sessionId: string }) {
  const { data: scans = [], isLoading, isError, refetch } = useStocktakeScanLog(
    sessionId,
    true
  )

  if (isLoading) {
    return (
      <div className="px-[18px] py-6 text-xs text-[var(--mute)]" data-testid="scan-log-loading">
        Loading the scan log…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center gap-3 px-[18px] py-6">
        <p className="text-xs text-[var(--status-rejected-fg)]">Could not load the scan log.</p>
        <Button
          variant="outline"
          size="sm"
          className="h-[22px] border-[var(--line-warm)] px-2 text-[11px]"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (scans.length === 0) {
    return (
      <div className="px-[18px] py-6 text-xs text-[var(--mute)]" data-testid="scan-log-empty">
        No scans yet.
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto" data-testid="scan-log">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col style={{ width: 170 }} />
          <col style={{ width: 150 }} />
          <col />
          <col style={{ width: 70 }} />
          <col style={{ width: 120 }} />
        </colgroup>
        <thead>
          <tr>
            <th className={TH}>Time</th>
            <th className={TH}>Barcode</th>
            <th className={TH}>Product</th>
            <th className={`${TH} text-right`}>Qty</th>
            <th className={TH}>Scanned By</th>
          </tr>
        </thead>
        <tbody>
          {scans.map((s) => (
            <tr key={s.scan_id} data-testid={`scan-log-row-${s.scan_id}`}>
              <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-xs tabular-nums whitespace-nowrap text-[var(--ink-2)]">
                {fmtDateTime(s.scan_timestamp) || '—'}
              </td>
              <td className="border-b border-[var(--line-warm)] px-2 py-1.5 font-mono text-xs text-[var(--ink-2)]">
                {s.barcode || '—'}
              </td>
              <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-xs">
                {s.variant_id == null ? (
                  <span
                    className="font-semibold text-[var(--status-rejected-fg)]"
                    data-testid={`scan-log-miss-${s.scan_id}`}
                  >
                    no match
                  </span>
                ) : (
                  <span className="truncate text-[var(--text-primary)]">
                    {s.product_title || '—'}
                    {s.variant_title && (
                      <span className="text-[var(--mute)]"> · {s.variant_title}</span>
                    )}
                  </span>
                )}
              </td>
              <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-right text-xs tabular-nums">
                {s.qty_scanned}
              </td>
              <td className="border-b border-[var(--line-warm)] px-2 py-1.5 text-xs text-[var(--ink-2)]">
                {s.scanned_by || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
